/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { STRING_TRUE } from '@/data/constants/catalog';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC } from '@/data/constants/dataKey';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { FULFILLMENT_METHOD } from '@/data/constants/inventory';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { PDP_REQUIRED_ATTRS, TYPES } from '@/data/constants/product';
import { useAllowableShippingModes } from '@/data/Content/_AllowableShippingModes';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useLoginRedirectRequired } from '@/data/Content/_LoginRedirectRequired';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getPromo, usePromo } from '@/data/Content/_Promos';
import { requisitionListItemUpdate } from '@/data/Content/_RequisitionList';
import { wishListUpdater } from '@/data/Content/_WishListDetails';
import { fetchDefaultWishlistOrCreateNew, wishListRemoverOrItemRemover } from '@/data/Content/_Wishlists';
import {
	addToCartFetcherV2 as addToCartFetcher,
	BASE_ADD_2_CART_BODY,
	useCart,
	useCartSWRKey,
} from '@/data/Content/Cart';
import { useCategory } from '@/data/Content/Category';
import { getFlexFlowStoreFeature } from '@/data/Content/FlexFlowStoreFeature-Server';
import { getInventoryV2, useInventoryV2 } from '@/data/Content/InventoryV2';
import { personMutatorKeyMatcher } from '@/data/Content/Login';
import { getProduct, getProductByKeyType, useProduct } from '@/data/Content/Product';
import { EventsContext } from '@/data/context/events';
import { StoreInventoryContext } from '@/data/context/storeInventory';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { getLocalization, useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { ProductType, Selection } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { StoreDetails } from '@/data/types/Store';
import { useUser } from '@/data/User';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getParentCategoryFromSlashPath } from '@/data/utils/getParentCategoryFromSlashPath';
import {
	getAttrsByIdentifier,
	mapProductDetailsData,
	search,
} from '@/data/utils/mapProductDetailsData';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import { WishlistWishlistItem } from 'integration/generated/transactions/data-contracts';
import { MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { mutate } from 'swr';
import { DEFAULT_DELIVERY_TYPE } from '../constants/product';
import { CLICK_AND_COLLECT, HOME_DELIVERY } from '../constants/clickAndCollect';
import { getCookie, setCookie } from '@/utils/clickAndCollect';
import { ErrorType } from '@/data/types/Error';
import { getAttributesValueByIdentifier, getAttributeValueByIdentifier } from '@/utils/productAttributes';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { useGetUserType } from '../_User';
import { useWishRequisitionList } from './_WishRequisitionList';
import { useNotifications } from './Notifications';
export { getAttrsByIdentifier };

type Props = {
	partNumber: string;
	/** @deprecated use `physicalStore` instead */
	physicalStoreName?: string;
	physicalStore?: StoreDetails;
};

const fetchLocalization = async ({ cache, context }: ContentProps, productId: string) =>
	await Promise.all([
		getPromo(cache, productId, context),
		getLocalization(cache, context.locale || 'nl-NL', 'productDetail'),
		getLocalization(cache, context.locale || 'nl-NL', 'CommerceEnvironment'),
		getLocalization(cache, context.locale || 'nl-NL', 'PriceDisplay'),
		getLocalization(cache, context.locale || 'nl-NL', 'Common'),
		getLocalization(cache, context.locale || 'nl-NL', 'StoreStock'),
		getLocalization(cache, context.locale || 'nl-NL', 'Inventory'),
		getLocalization(cache, context.locale || 'nl-NL', 'PdpExternalVideo'),
		getLocalization(cache, context.locale || 'nl-NL', 'ClickAndCollect'),
	]);

export const getProductDetails = async ({ cache, id, context }: ContentProps) => {
	const partNumber = id.toString();
	let product = await getProduct(cache, partNumber, context);
	if (product?.type === 'item' && product.parentCatalogEntryID) {
		// get parent product
		product = await getProductByKeyType(cache, 'id', product.parentCatalogEntryID, context);
	} // remove inventory for edge cache
	await getFlexFlowStoreFeature({ cache, id: EMS_STORE_FEATURE.GUEST_SHOPPING, context });
	await fetchLocalization({ cache, id, context }, product?.id ?? '');
	await getInventoryV2(cache, partNumber, context);
	return product as ProductType 
};

export const useProductDetails = (props: Props) => {
	const { onProductView, onAddToCart, onAddToWishlist } = useContext(EventsContext);
	const { showSuccessMessage, showErrorMessage, notifyError,setCustomMessageVariables } = useNotifications();
	const wlNls = useLocalization('WishList');
	const success = useLocalization('success-message');
	const errorMessages = useLocalization('error-message');
	const { settings } = useSettings();
	const router = useNextRouter();
	const { langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { partNumber: pnFromPage, physicalStore } = props;
	const { product: inputCE } = useProduct({ id: pnFromPage });
	const { loginStatus, redirectToLoginIfNeed: loginRequired } = useLoginRedirectRequired();
	const { product: root } = useProduct({
		id: inputCE?.parentCatalogEntryID,
		isCEId: true,
		condition: inputCE?.type === 'item',
	});
	const { selection: initial, product } = useMemo(
		() => mapProductDetailsData(inputCE, root),
		[inputCE, root]
	);
	const [selection, setSelection] = useState<Selection>(initial as Selection);
	const { ga4, ua } = getGTMConfig(settings);
	const { category } = useCategory(
		ga4 || ua ? getParentCategoryFromSlashPath(product?.parentCatalogGroupID) : ''
	);
	//const { promos } = usePromo(product?.id); // Not needed here, calling the usePromo hook directly from Promo UI component
	const { user } = useUser();
	const currentCartSWRKey = useCartSWRKey(); // in current language
	const isGenericUser = user?.isGeneric ?? false;	
	const { actions, storeLocator } = useStoreLocatorState();
	const {
		availability,
		hasInventory,
		isLoading: isInventoryLoading,
	} = useInventoryV2({
		partNumber: (selection ?? initial)?.sku?.partNumber,
		physicalStore,
	});
	const { pickupInStoreShipMode, deliveryShipMode } = useAllowableShippingModes();
	const { wishLists, requisitionLists, mutateWishlistData } = useWishRequisitionList();
	const { setOpen } = useContext(StoreInventoryContext);
	if(product){			
		product.stockAvailable = availability?.at(0)?.status??false;
	}

	const { isOrderHasPickupOnlyItem } = useCart();

	const productAttrValues = useMemo(()=> getAttributesValueByIdentifier(product?.attributes,PDP_REQUIRED_ATTRS),[product]);

	const [isDeliverySelected, setIsDeliverySelected] = useState<boolean>(true);
	const INITIAL_DELIVERY_TYPE = productAttrValues.ITNL_PickupInStoreOnly === "true" ? CLICK_AND_COLLECT : DEFAULT_DELIVERY_TYPE;
	const [deliveryType, setDeliveryType] = useState(INITIAL_DELIVERY_TYPE);
	const isPickupOnlyItem = productAttrValues.ITNL_PickupInStoreOnly === "true";

	const selectBox = useCallback(
		(name: string) => (_event: MouseEvent<HTMLElement>) =>
			setIsDeliverySelected(name !== FULFILLMENT_METHOD.PICKUP),
		[]
	);

	const onSelectStore = useCallback(
		(event: MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			setOpen(true);
		},
		[setOpen]
	);

	/**
	 * Defining attribute change handler
	 * @param identifier new attribute identifier
	 * @param value new attribute value
	 */
	const onAttributeChange = useCallback(
		(identifier: string, value: string) => {
			if (product?.items) {
				setSelection((selection) => {
					const asAttrs = product.items.map((s) => getAttrsByIdentifier(s.definingAttributes));
					const fullAttr = {
						...(selection ?? initial).attrsByIdentifier,
						[identifier]: value,
					};
					const { exact, best } = search(fullAttr, identifier, asAttrs);
					let sku: ProductType | undefined = undefined;
					let attrsByIdentifier: Record<string, string> | undefined = undefined;

					if (exact !== -1) {
						attrsByIdentifier = fullAttr;
						sku = product.items[exact];
					} else if (best !== -1) {
						attrsByIdentifier = { ...asAttrs[best] };
						sku = product.items[best];
					} else {
						sku = {} as ProductType;
					}
					return {
						...(selection ?? initial),
						buyable: !!(sku?.buyable === STRING_TRUE),
						sku,
						attrsByIdentifier,
					};
				});
			}
		},
		[initial, product?.items]
	);

	const onQuantity = useCallback(
		(quantity: number | null) =>
			setSelection((selection) => ({
				...(selection ?? initial),
				quantity: quantity ?? 0,
			})),
		[initial]
	);

	const validatedAvailability = useCallback(
		() =>
			isDeliverySelected
				? availability?.find((a) => a.status)
				: availability?.find((a) => a.physicalStoreId),
		[availability, isDeliverySelected]
	);

	const isB2bUser: Boolean = useGetUserType(user);
	const skipMaxOrderForB2BUser = settings?.userData?.SkipMaxOrderForB2BUser === "true";
	const constructOrderItem = useCallback(
		(availability: ProductAvailabilityData) => {
			const { sku, quantity } = selection ?? initial;
			const maxOrderLimit:string = getAttributeValueByIdentifier(product?.attributes,"ITNL_MaximumOrderLimit");
			return {
				partNumber: sku?.partNumber,
				quantity: quantity.toString(),
				productId:sku?.id,
					xitem_productName:product?.name,
					xitem_maxOrderLimit:(isB2bUser && skipMaxOrderForB2BUser)?'':maxOrderLimit,
				...(isDeliverySelected
					? { shipModeId: deliveryShipMode?.shipModeId }
					: {
							physicalStoreId: availability.physicalStoreId,
							shipModeId: pickupInStoreShipMode?.shipModeId,
					  }),
			};
		},
		[deliveryShipMode, initial, isDeliverySelected, pickupInStoreShipMode, selection]
	);
	const addToCart = useCallback(
		async (event: MouseEvent<HTMLElement>) => {
			const selectedStoreId = storeLocator?.selectedStore?.id || getCookie("WC_pickUpStore");
			if(deliveryType === CLICK_AND_COLLECT && !selectedStoreId){
				const pickUpChkBoxDisabled = document.querySelector<HTMLInputElement>("#checkbox_deliveryType_pickUp")?.disabled ?? false;
				notifyError({
					type: "common-error",
					messageKey:
						pickUpChkBoxDisabled
							? "_ERR_PDP_CLICK_AND_COLLECT_NOT_AVAILABLE"
							: "_ERR_PDP_SELECT_STORE_BEFORE_ADD2CART"
				} as ErrorType);
				return;
			}

			if(deliveryType === HOME_DELIVERY) {
				let errorMsg = isOrderHasPickupOnlyItem 
							? "_ERR_HOME_DELIVERY_UNAVAILABLE_PICKUP_ONLY"
							: isPickupOnlyItem
							? "_ERR_PICKUP_ONLY_ITEM"
							: EMPTY_STRING
				if (errorMsg !== EMPTY_STRING) {
					notifyError({ type: "common-error", messageKey:errorMsg } as ErrorType);
					return;
				}
			}

			if (await loginRequired()) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			const { sku, quantity } = selection ?? initial;
			if (sku?.partNumber) {
				// the availability of online or physical store
				const avail = validatedAvailability();
				if (!isDeliverySelected && !avail) {
					showErrorMessage(errorMessages.SelectStoreToAddToCart.t());
					return;
				}

				const orderItem = constructOrderItem(avail as ProductAvailabilityData);

				const data = { ...BASE_ADD_2_CART_BODY, orderItem: [orderItem], "x_orderField3":deliveryType };
				try {
					await addToCartFetcher(isGenericUser)(settings?.storeId ?? '', {}, data, params);
					if (isGenericUser) {
						await mutate(personMutatorKeyMatcher(EMPTY_STRING)); // current page
						await mutate(
							personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC),
							undefined
						);
					}
					await mutate(cartMutatorKeyMatcher(EMPTY_STRING));
					await mutate(cartMutatorKeyMatcher(currentCartSWRKey), undefined); // cart in other languages
					// notification
					showSuccessMessage(success.ITEM_TO_CART.t([product?.name ?? '']), true);

					onAddToCart({
						gtm: {
							selection,
							category,
							quantity,
							orgName: '', // TODO: specify selected org-name
							orgId: '', // TODO: specify selected org
							storeName: settings.storeName,
							settings,
						},
					});

					if (deliveryType === CLICK_AND_COLLECT) { // This logic should run only if the acc2cart cation is successful , hence moving it here
						if (selectedStoreId) setCookie("WC_pickUpStore", selectedStoreId);
					} else {
						setCookie("WC_pickUpStore", "");
						actions?.resetSelectedStore();
					}

					scrollTo(0, 0);
				}  catch (e) {
					const parsed = processError(e as TransactionErrorResponse);
					if ((parsed as ErrorType)?.error.errorKey === '_API_BAD_INV') {
						const errorParams = (parsed as ErrorType)?.error?.errorParameters;
						const match = errorParams?.match(/\],\d+,(.*)$/);
						const productName = match?.[1]?.trim() ?? '';
						notifyError({
							type: 'common-error',
							messageKey: 'ERR_OOS_ITEM_IN_CART',
							errorParameters: [productName],
						} as ErrorType);
					} else {
						notifyError(parsed);
					}
				}
			}
		},
		[
			loginRequired,
			selection,
			initial,
			validatedAvailability,
			isDeliverySelected,
			constructOrderItem,
			showErrorMessage,
			errorMessages,
			isGenericUser,
			settings,
			params,
			showSuccessMessage,
			success,
			product,
			onAddToCart,
			category,
			notifyError,
			currentCartSWRKey,
			deliveryType,
			isOrderHasPickupOnlyItem,
			isPickupOnlyItem
		]
	);

	const addToWishList = useCallback(
		(partNumber: string, quantity: number, wishList: WishlistWishlistItem) => async () => {
			try {
				const rc = await wishListUpdater(true)(
					settings?.storeId as string,
					wishList.uniqueID as string,
					{ item: [{ partNumber, quantityRequested: quantity.toString() } as any] },
					{ addItem: true },
					params
				);
				if (rc) {
					setCustomMessageVariables({message:success.WISHLIST_ADD_SUCCESS.t(), isAddToWishList:true});
				}
				product && onAddToWishlist(product, quantity);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[
			settings?.storeId,
			params,
			product,
			onAddToWishlist,
			showSuccessMessage,
			success.WISHLIST_ADD_SUCCESS,
			notifyError,
		]
	);
const removeFromWishList = useCallback(
		(productId: string, itemId: string,externalId: string) => async () => {
			try {
				const rc = await wishListRemoverOrItemRemover(true)(
					settings?.storeId as string,
					externalId as string,
					{ productId:itemId.toString()} as any,
					params
				);
				if (rc) {
					setCustomMessageVariables({message:success.DELETE_WISHLIST_ITEM_SUCCESS.t(), isAddToWishList:true});
				}
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[
			settings?.storeId,
			params,
			product,
			onAddToWishlist,
			showSuccessMessage,
			success.DELETE_WISHLIST_ITEM_SUCCESS,
			notifyError,
		]
	);

	const addToDefaultWishlist = useCallback(
		(partNumber: string, quantity: number) => async () => {
			try {
				const { wishlistId, wishlistName } = await fetchDefaultWishlistOrCreateNew(true)(
					settings?.storeId,
					undefined,
					wlNls.DefaultWishListName.t(),
					params
				);
				await addToWishList(partNumber, quantity, {
					uniqueID: wishlistId,
					description: wishlistName,
				} as WishlistWishlistItem)();
				await mutateWishlistData(undefined);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[wlNls, settings?.storeId, params, addToWishList, notifyError]
	);

	const addToRequisitionList = useCallback(
		(requisitionListId: string) => async () => {
			const { sku, quantity } = selection ?? initial;
			if (sku?.partNumber) {
				try {
					await requisitionListItemUpdate(true)(
						{
							storeId: settings.storeId,
							langId,
							requisitionListId,
							data: { partNumber: sku.partNumber, quantity },
						},
						params
					);
					showSuccessMessage(success.addItemListSuccessfully.t({ v: sku.name }));
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			}
		},
		[initial, notifyError, params, selection, settings.storeId, showSuccessMessage, success, langId]
	);

	useEffect(() => {
			product &&
				(product.type !== TYPES.sku || !product.parentCatalogEntryID) &&
				onProductView({
					gtm: { product, storeName: settings.storeName, settings },
					marketing: {
						productId: product.id,
						settings,
						params,
					}, 
				}); 
		}, [onProductView, product]);

	useEffect(() => {
		setSelection(initial as Selection);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [langId]);

	const calculatedPrice= useMemo(()=>product?.price.find(price =>  price.usage === 'Offer')?.value ?? '',[product]);

	return {
		onQuantity,
		selection: selection ?? initial,
		product,
		availability,
		onAttributeChange,
		addToWishList,
		addToCart,
		hasInventory,
		loginStatus,
		//promos,
		wishLists,
		loginRequired,
		pickupInStoreShipMode,
		requisitionLists,
		addToRequisitionList,
		category,
		addToDefaultWishlist,
		isInventoryLoading,
		isDeliverySelected,
		selectBox,
		physicalStore,
		onSelectStore,
		deliveryType,
		setDeliveryType,
		calculatedPrice,
		productAttrValues,
		removeFromWishList,
		mutateWishlistData,
		isPickupOnlyItem
	};
};